import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, Trophy, Users, Zap, ArrowRight, Plus } from "lucide-react";
import TournamentBracket from "./tournament-bracket";
import { convertToLocalCurrency } from "../../../../utils/currencyUtils";
import "../styles/hero.css";
import { useAuth } from "../../../../common/store/authStore";

function HeroSection() {
  const [prizePool, setPrizePool] = useState("$7.5K+");
  const { authenticatedUser } = useAuth();
  const isAuthenticated = !!authenticatedUser;

  useEffect(() => {
    const localCurrency = convertToLocalCurrency(7500);
    setPrizePool(localCurrency.formatted);
  }, []);

  return (
    <section className="hero-section">
      {/* Background Elements */}
      <div className="hero-background">
        <div className="hero-overlay"></div>
        <div className="floating-elements">
          <div className="floating-element element-1"></div>
          <div className="floating-element element-2"></div>
          <div className="floating-element element-3"></div>
        </div>
      </div>

      <div className="hero-container">
        <div className="hero-content">
          {/* Left Side - Text Content */}
          <div className="hero-text">
            <div className="hero-badge">
              <Trophy size={16} />
              <span>#1 Gaming Platform in Africa</span>
            </div>

            <h1 className="hero-title">
              <span className="hero-title-line">Earn While You</span>
              <span className="hero-title-line highlight">Play & Host</span>
              <span className="hero-title-line">Tournaments</span>
            </h1>

            <p className="hero-description">
              Connect with gamers across Africa. Host tournaments, stream your
              gameplay, and earn rewards while building the ultimate gaming
              community.
            </p>

            <div className="hero-stats">
              <div className="stat-home-item">
                <Users size={20} />
                <div>
                  <span className="stat-number">1.5K+</span>
                  <span className="stat-label">Active Gamers</span>
                </div>
              </div>
              <div className="stat-home-item">
                <Trophy size={20} />
                <div>
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Tournaments</span>
                </div>
              </div>
              <div className="stat-home-item">
                <Zap size={20} />
                <div>
                  <span className="stat-number">{prizePool}</span>
                  <span className="stat-label">Prize Pool</span>
                </div>
              </div>
            </div>

            <div className="hero-actions">
              {isAuthenticated ? (
                <Link to="/create-tournament" className="hero-btn primary">
                  <Plus size={18} stroke="#000" />
                  <span>Create Tournament</span>
                </Link>
              ) : (
                <Link to="/sign-up" className="hero-btn primary">
                  <span>Get Started</span>
                  <ArrowRight size={18} stroke="#000" />
                </Link>
              )}
              <Link to="/tournaments" className="hero-btn secondary">
                <Play size={18} />
                <span>Browse Tournaments</span>
              </Link>
            </div>
            {/* Supported Countries Section - glassy, flag poles, swaying flags */}
            <div className="supported-countries glassy-flags-bg">
              <h4
                style={{ fontWeight: 600, marginBottom: 8, textAlign: "start" }}
              >
                We support tournaments in:
              </h4>
              <div className="flag-section">
                {/* Kenya Flag SVG */}
                <div className="flag-pole-container">
                  <span
                    className="flag-svg sway-flag"
                    style={{ marginLeft: 8 }}
                  >
                    <svg
                      width="32"
                      height="20"
                      viewBox="0 0 32 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="20" fill="#fff" />
                      <rect y="0" width="32" height="6.66" fill="#000" />
                      <rect y="6.66" width="32" height="6.66" fill="#FF0000" />
                      <rect y="13.32" width="32" height="6.68" fill="#006600" />
                      <ellipse
                        cx="16"
                        cy="10"
                        rx="5"
                        ry="8"
                        fill="#fff"
                        stroke="#000"
                        strokeWidth="1.5"
                      />
                      <ellipse
                        cx="16"
                        cy="10"
                        rx="2.5"
                        ry="4"
                        fill="#FF0000"
                        stroke="#000"
                        strokeWidth="1"
                      />
                    </svg>
                  </span>
                  <span style={{ fontWeight: 500, marginLeft: 8 }}>Kenya</span>
                </div>
                {/* Nigeria Flag SVG */}
                <div className="flag-pole-container">
                  <span
                    className="flag-svg sway-flag"
                    style={{ marginLeft: 8 }}
                  >
                    <svg
                      width="32"
                      height="20"
                      viewBox="0 0 32 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="20" fill="#fff" />
                      <rect width="10.66" height="20" fill="#008753" />
                      <rect
                        x="21.33"
                        width="10.67"
                        height="20"
                        fill="#008753"
                      />
                    </svg>
                  </span>
                  <span style={{ fontWeight: 500, marginLeft: 8 }}>
                    Nigeria
                  </span>
                </div>
                {/* Ghana Flag SVG */}
                <div className="flag-pole-container">
                  <span
                    className="flag-svg sway-flag"
                    style={{ marginLeft: 8 }}
                  >
                    <svg
                      width="32"
                      height="20"
                      viewBox="0 0 32 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="6.66" y="0" fill="#EF3340" />
                      <rect width="32" height="6.66" y="6.66" fill="#FFD100" />
                      <rect width="32" height="6.68" y="13.32" fill="#009739" />
                      <polygon
                        points="16,7.5 17.2,11.2 21,11.2 17.9,13.2 19.1,16.8 16,14.5 12.9,16.8 14.1,13.2 11,11.2 14.8,11.2"
                        fill="#000"
                      />
                    </svg>
                  </span>
                  <span style={{ fontWeight: 500, marginLeft: 8 }}>Ghana</span>
                </div>
                {/* Tanzania Flag SVG */}
                <div className="flag-pole-container">
                  <span
                    className="flag-svg sway-flag"
                    style={{ marginLeft: 8 }}
                  >
                    <svg
                      width="32"
                      height="20"
                      viewBox="0 0 32 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <polygon points="0,0 0,20 32,20" fill="#00A3DD" />
                      <polygon points="0,0 32,0 32,20" fill="#1EB53A" />
                      <polygon points="-2,20 34,0 36,4 0,24" fill="#FFD600" />
                      <polygon points="-1,20 33,0 35,4 1,24" fill="#000" />
                      <polygon
                        points="0,20 32,0 32,20"
                        fill="none"
                        stroke="#FFD600"
                        strokeWidth="2"
                      />
                    </svg>
                  </span>
                  <span style={{ fontWeight: 500, marginLeft: 8 }}>
                    Tanzania
                  </span>
                </div>
                {/* Uganda Flag SVG */}
                <div className="flag-pole-container">
                  <span
                    className="flag-svg sway-flag"
                    style={{ marginLeft: 8 }}
                  >
                    <svg
                      width="32"
                      height="20"
                      viewBox="0 0 32 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="20" fill="#FFD600" />
                      <rect y="3.33" width="32" height="3.33" fill="#000" />
                      <rect y="6.66" width="32" height="3.33" fill="#FF0000" />
                      <rect y="10" width="32" height="3.33" fill="#FFD600" />
                      <rect y="13.33" width="32" height="3.33" fill="#000" />
                      <rect y="16.66" width="32" height="3.34" fill="#FF0000" />
                      <circle
                        cx="16"
                        cy="10"
                        r="4"
                        fill="#fff"
                        stroke="#000"
                        strokeWidth="1"
                      />
                      <text
                        x="16"
                        y="13"
                        textAnchor="middle"
                        fontSize="6"
                        fontWeight="bold"
                        fill="#000"
                      >
                        🦄
                      </text>
                    </svg>
                  </span>
                  <span style={{ fontWeight: 500, marginLeft: 8 }}>Uganda</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Bracket Component */}
          <div className="hero-visuals">
            <div className="bracket-container">
              <TournamentBracket />
            </div>
            <div className="hero-glow"></div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
